import { el, clear } from "../utils/dom.ts";
import { getIndicatorsWeekly, type WeeklyIndicator } from "../services/api-client.ts";
import { DOMAIN_LABELS, type Domain } from "../../functions/_shared/event-taxonomy.ts";
import { signalSourceLabel } from "./signal-source-label.ts";

// National domain ranking for the most recent completed week + delta vs the week
// before. Given province coverage is ~2% at launch, ranking by DOMAIN (which has
// real national data) is the meaningful view; a province ranking would rank mostly
// zeros. Horizontal bars.
export class DomainRankingPanel {
  readonly el: HTMLElement;
  private body: HTMLElement;

  constructor() {
    this.body = el("div", { className: "ranking-body" }, ["Đang tải…"]);
    this.el = el("section", { className: "panel ranking-panel" }, [
      el("div", { className: "panel-head" }, [
        el("h2", {}, ["Xếp hạng lĩnh vực tuần"]),
        signalSourceLabel("media"),
      ]),
      this.body,
    ]);
  }

  async load(): Promise<void> {
    try {
      // National rows across all domains (province=national selects '').
      const { items } = await getIndicatorsWeekly("?province=national&weeks=12");
      this.render(items);
    } catch {
      clear(this.body);
      this.body.append(el("p", { className: "muted" }, ["Không tải được dữ liệu."]));
    }
  }

  private render(rows: WeeklyIndicator[]): void {
    clear(this.body);
    if (rows.length === 0) {
      this.body.append(el("p", { className: "muted" }, ["Chưa có dữ liệu tuần."]));
      return;
    }
    const weeks = [...new Set(rows.map((r) => r.week))].sort();
    const latest = weeks[weeks.length - 1];
    const prev = weeks[weeks.length - 2];

    const nowByDomain = new Map<string, number>();
    const prevByDomain = new Map<string, number>();
    for (const r of rows) {
      if (r.week === latest) nowByDomain.set(r.domain, r.event_count);
      if (r.week === prev) prevByDomain.set(r.domain, r.event_count);
    }

    const ranked = [...nowByDomain.entries()].sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...ranked.map(([, v]) => v));

    const list = el("div", { className: "ranking-list" });
    for (const [domain, count] of ranked) {
      const before = prevByDomain.get(domain) ?? 0;
      const delta = count - before;
      const deltaLabel = delta === 0 ? "—" : delta > 0 ? `▲ ${delta}` : `▼ ${Math.abs(delta)}`;
      const deltaClass = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
      list.append(
        el("div", { className: "ranking-row" }, [
          el("span", { className: "ranking-label" }, [DOMAIN_LABELS[domain as Domain] ?? domain]),
          el("span", { className: "ranking-bar-wrap" }, [
            el("span", {
              className: "ranking-bar",
              style: `width: ${Math.round((count / max) * 100)}%`,
            }),
          ]),
          el("span", { className: "ranking-count" }, [String(count)]),
          el("span", { className: `ranking-delta ${deltaClass}` }, [deltaLabel]),
        ]),
      );
    }
    this.body.append(
      el("div", { className: "ranking-week muted" }, [`Tuần ${latest} (so với ${prev ?? "—"})`]),
      list,
    );
  }
}
